import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppSelector } from '@/redux/hooks'

import type { FtuConfig, FtuStep } from '../types/types'

export type FtuStepMachine = {
  currentStep: FtuStep
  stepIndex: number
  stepCount: number
  isFirstStep: boolean
  isLastStep: boolean
  // Whether the primary Next/Finish button is enabled.
  canProceed: boolean
  // True when a gated step's required action is currently impossible (i.e. user must press Next)
  isActionImpossible: boolean
  next: () => void
  back: () => void
  finish: () => void
}

export const useFtuStepMachine = (
  ftu: FtuConfig,
  onFinish: () => void
): FtuStepMachine => {
  const [stepIndex, setStepIndex] = useState(0)
  const [latchedSteps, setLatchedSteps] = useState<Record<string, boolean>>({})

  const stepCount = ftu.steps.length
  // stepIndex is always clamped to a valid index and configs are non-empty.
  const currentStep = ftu.steps[stepIndex] as FtuStep
  const isLastStep = stepIndex === stepCount - 1

  const isGateSatisfied = useAppSelector((state) =>
    currentStep.kind === 'gated' ? currentStep.isComplete(state) : true
  )
  const canAttempt = useAppSelector((state) =>
    currentStep.kind === 'gated' && currentStep.canAttempt
      ? currentStep.canAttempt(state)
      : true
  )

  const isLatched = latchedSteps[currentStep.id] ?? false

  // Remember once a latching gate has been satisfied.
  useEffect(() => {
    if (
      currentStep.kind === 'gated' &&
      currentStep.latch &&
      isGateSatisfied &&
      !isLatched
    ) {
      setLatchedSteps((prev) => ({ ...prev, [currentStep.id]: true }))
    }
  }, [currentStep, isGateSatisfied, isLatched])

  const isGated = currentStep.kind === 'gated'
  const isEffectivelySatisfied = isGateSatisfied || isLatched
  const isActionImpossible = isGated && !canAttempt
  const canProceed = !isGated || isEffectivelySatisfied || isActionImpossible

  const next = useCallback(() => {
    setStepIndex((index) => Math.min(index + 1, stepCount - 1))
  }, [stepCount])

  const back = useCallback(() => {
    setStepIndex((index) => Math.max(index - 1, 0))
  }, [])

  const finish = useCallback(() => {
    onFinish()
  }, [onFinish])

  // Auto-advance keyboard-style gated steps the instant the gate opens — but
  // only when it opens while the user is viewing the step, not when they
  // navigate *back* into a step whose gate is already satisfied. Without this
  // rising-edge check, pressing Back from a later step into an already-open
  // gated step would immediately bounce the user forward again (making Back
  // appear broken).
  const autoAdvanceBaselineRef = useRef({
    stepIndex,
    wasSatisfied: isEffectivelySatisfied,
  })
  useEffect(() => {
    const baseline = autoAdvanceBaselineRef.current
    const hasEnteredNewStep = baseline.stepIndex !== stepIndex
    const hasGateJustOpened =
      !hasEnteredNewStep && !baseline.wasSatisfied && isEffectivelySatisfied

    autoAdvanceBaselineRef.current = {
      stepIndex,
      wasSatisfied: isEffectivelySatisfied,
    }

    if (
      isGated &&
      currentStep.autoAdvanceOnComplete === true &&
      hasGateJustOpened &&
      !isLastStep
    ) {
      next()
    }
  }, [
    stepIndex,
    isEffectivelySatisfied,
    isGated,
    currentStep,
    isLastStep,
    next,
  ])

  return {
    currentStep,
    stepIndex,
    stepCount,
    isFirstStep: stepIndex === 0,
    isLastStep,
    canProceed,
    isActionImpossible,
    next,
    back,
    finish,
  }
}
